/**
 * v3.metrics — /v3/metrics/dashboard, /v3/metrics/cart-resolution.
 */

import type {
  RequestOptions,
  ApiResult,
  MetricsDashboard,
  CartResolutionMetricsResponse,
  CartResolutionRange,
  CommitTimingMetricsResponse,
  CommitTimingRange,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface MetricsDashboardResponse {
  success: boolean;
  windowDays: number;
  branch?: string;
  region?: string;
  methodology: MetricsDashboard['methodology'];
  kpis: MetricsDashboard['kpis'];
  slaByStage: MetricsDashboard['slaByStage'];
  dailyCounts: MetricsDashboard['dailyCounts'];
  error?: string;
}

export interface CartResolutionRequestOptions {
  branch?: string;
  origin?: string;
}

/** Officer Home KPI tiles (Bill 2026-07-13 Item 5). Counts period-toggled. */
export interface HomeMetricsResponse {
  scope: { officerCode?: string; branch?: string };
  accountsOpened: { month: number; ytd: number; all: number };
  adverseActions: { month: number; ytd: number; all: number };
  avgCartDuration: {
    inBranchMs: number;
    inBranchMinutes: number;
    onlineMs: number;
    onlineMinutes: number;
  };
  generatedAt: string;
}

export class MetricsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public dashboard(
    req: { days: number; branch?: string; region?: string },
    opts?: RequestOptions
  ): Promise<MetricsDashboardResponse> {
    return this.http.get<MetricsDashboardResponse>('/v3/metrics/dashboard', {
      ...opts,
      query: req as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Wave 6.2 RESOLUTION-METRICS-KPI — cart-resolution KPI bundle.
   *
   * @param range  Time window: 24h | 7d | 30d | 90d | ytd
   * @param opts.branch  Branch code filter (omit for all branches)
   * @param opts.origin  Origin role filter (e.g. 'fsr-self', 'lobby-queue', 'rcp-portal')
   */
  public cartResolution(
    range: CartResolutionRange,
    opts?: CartResolutionRequestOptions & RequestOptions
  ): Promise<CartResolutionMetricsResponse> {
    const { branch, origin, ...rest } = opts ?? {};
    const query: Record<string, string | number | boolean | undefined> = { range };
    if (branch) query.branch = branch;
    if (origin) query.origin = origin;
    return this.http.get<CartResolutionMetricsResponse>('/v3/metrics/cart-resolution', {
      ...rest,
      query,
    });
  }

  /**
   * Wave 7 OBSERVABILITY — commit-pipeline timing breakdown.
   *
   * Returns per-step p50/p95 latency, success rate per step, and the
   * top-failing step in the selected range.
   *
   * @param range  Time window: 24h | 7d | 30d | 90d | ytd
   */
  public commitTiming(
    range: CommitTimingRange,
    opts?: RequestOptions
  ): Promise<CommitTimingMetricsResponse> {
    return this.http.get<CommitTimingMetricsResponse>('/v3/metrics/commit-timing', {
      ...opts,
      query: { range } as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Officer Home KPI tiles (Bill 2026-07-13 Item 5): Accounts Opened
   * (month/ytd/all), Adverse Actions (month/ytd/all), Avg Cart Duration
   * (in-branch vs online). Scoped to the officer when officerCode is given.
   */
  public home(
    req?: { officerCode?: string; branch?: string },
    opts?: RequestOptions
  ): Promise<HomeMetricsResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (req?.officerCode) query.officerCode = req.officerCode;
    if (req?.branch) query.branch = req.branch;
    return this.http.get<HomeMetricsResponse>('/v3/metrics/home', { ...opts, query });
  }
}

export type { ApiResult };
